import { useState, useEffect } from 'react';
import type { ReactElement } from 'react';
import { RefreshCw, Smartphone, Laptop, Monitor, Shield, AlertCircle, Activity, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/common/DataTable';
import type { Column } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { appDeviceService } from '../services';
import type { AppDevice } from '../services';
import { formatDate } from '../utils/formatDate';
import { useSettingsStore } from '../stores/settingsStore';
import StatCard from '../components/common/StatCard';
import { UserPlus } from 'lucide-react';
import InviteVPNUsersModal, { type InviteData } from '../components/admin/InviteVPNUsersModal';
import { useAuthStore } from '../stores/authStore';
import { useTenantStore } from '../stores/tenantStore';
import { vpnUserService } from '../services';
import { useToast } from '../components/common/ToastContainer';
import { usePermissions } from '../hooks/usePermissions';
import { systemService } from '../services';
import { appDeviceProtocolLabel, isAccessPlaneDevice } from '../utils/appDeviceProtocol';

interface DeviceRow {
  id: string;
  name: string;
  platform: string;
  platformIcon: ReactElement;
  status: 'active' | 'inactive' | 'suspended' | 'revoked';
  lastSeen: string;
  connectedEdge?: string;
  /** The connection is to an Access gateway (VSNP-1626 W.6), not a v2 edge. */
  connectedViaAccess?: boolean;
  assignedIp?: string;
  dataTransfer?: string;
  groupName: string;
  createdAt: string;
  tunnelProtocol?: string;
  plane?: string;
}

const AppDevices = () => {
  const navigate = useNavigate();
  const { getDateFormatSettings } = useSettingsStore();
  const toast = useToast();
  const { canCreate } = usePermissions();
  const { user: currentUser } = useAuthStore();
  const { selectedTenantId, getSelectedTenant } = useTenantStore();
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [groupNameMap, setGroupNameMap] = useState<Map<string, string>>(new Map());
  const [orgCode, setOrgCode] = useState<string>('');

  const selectedTenant = getSelectedTenant();
  const tenantId = currentUser?.type === 1 ? selectedTenantId : (currentUser?.tenantId || '');
  const tenantName = selectedTenant?.name || 'Your Tenant';

  const getPlatformIcon = (platform: string): ReactElement => {
    switch (platform.toLowerCase()) {
      case 'ios':
        return <Smartphone className="w-4 h-4 text-gray-600" />;
      case 'android':
        return <Smartphone className="w-4 h-4 text-green-600" />;
      case 'windows':
        return <Monitor className="w-4 h-4 text-blue-600" />;
      case 'macos':
        return <Laptop className="w-4 h-4 text-gray-700" />;
      case 'linux':
        return <Monitor className="w-4 h-4 text-orange-600" />;
      default:
        return <Monitor className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatDataTransfer = (rxBytes?: number, txBytes?: number): string => {
    if (!rxBytes && !txBytes) return 'No data';
    const formatBytes = (bytes: number) => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    };
    const rx = rxBytes || 0;
    const tx = txBytes || 0;
    return `↓${formatBytes(rx)} ↑${formatBytes(tx)}`;
  };

  // Map API device to UI device format
  const mapApiDeviceToUi = (apiDevice: AppDevice, nameMap?: Map<string, string>): DeviceRow => ({
    id: apiDevice.id,
    name: apiDevice.name,
    platform: apiDevice.platform,
    platformIcon: getPlatformIcon(apiDevice.platform),
    status: apiDevice.status,
    lastSeen: apiDevice.lastSeenAt
      ? formatDate(apiDevice.lastSeenAt, getDateFormatSettings())
      : 'Never',
    // An Access-plane device has no v2 peer: its connection is the open Secure
    // Access session, on the gateway the API names (VSNP-1626 W.6).
    connectedEdge: apiDevice.peer?.edgeName || apiDevice.peer?.edgeId
      || (apiDevice.access?.connected ? apiDevice.access.gatewayName || apiDevice.access.gatewayId || 'Access gateway' : undefined),
    connectedViaAccess: !apiDevice.peer && !!apiDevice.access?.connected,
    assignedIp: apiDevice.peer?.assignedIp,
    dataTransfer: formatDataTransfer(apiDevice.peer?.rxBytes, apiDevice.peer?.txBytes),
    groupName: apiDevice.deviceGroupId
      ? (nameMap || groupNameMap).get(apiDevice.deviceGroupId) || 'Unknown Group'
      : 'Unassigned',
    createdAt: formatDate(apiDevice.createdAt, getDateFormatSettings()),
    tunnelProtocol: apiDevice.tunnelProtocol,
    plane: apiDevice.plane,
  });

  // Fetch devices from API (admin view - all tenant devices)
  const fetchDevices = async () => {
    try {
      setLoading(true);

      // Fetch groups for name resolution
      const groupsResponse = await appDeviceService.listDeviceGroups({ page: 1, limit: 200 });
      const nameMap = new Map<string, string>();
      if (groupsResponse.items) {
        for (const g of groupsResponse.items) {
          nameMap.set(g.id, g.name);
        }
      }
      setGroupNameMap(nameMap);

      // Use listAllDevices for admin view (tenant-scoped)
      const response = await appDeviceService.listAllDevices();

      if (response.items && Array.isArray(response.items)) {
        const uiDevices = response.items.map((d: AppDevice) => mapApiDeviceToUi(d, nameMap));
        setDevices(uiDevices);
      } else {
        setDevices([]);
      }
    } catch (err) {
      console.error('Failed to fetch app devices:', err);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUsers = async (data: InviteData) => {
    try {
      const response = await vpnUserService.provision({
        emails: data.emails,
        tenantId: data.tenantId,
        emailTemplateId: data.emailTemplateId,
        expiresInDays: data.expiresInDays,
        deviceGroupIds: data.deviceGroupIds,
      });

      if (response.provisioned > 0) {
        toast.success(
          'Invitations Sent',
          `Successfully invited ${response.provisioned} VPN user${response.provisioned > 1 ? 's' : ''}`
        );
      }

      if (response.failed > 0) {
        const failedEmails = response.results
          .filter((r) => r.status !== 'invited')
          .map((r) => `${r.email} (${r.status})`)
          .join(', ');
        toast.warning(
          'Some Invitations Failed',
          `Failed to invite ${response.failed} VPN user${response.failed > 1 ? 's' : ''}: ${failedEmails}`
        );
      }
    } catch (error) {
      console.error('Failed to provision VPN users:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchDevices();
    // Fetch org code from Global VSA
    systemService.getOrgCode().then(setOrgCode).catch(() => {});
  }, []);

  const columns: Column<DeviceRow>[] = [
    {
      key: 'name',
      label: 'Device Name',
      render: (row) => (
        <button
          onClick={() => navigate(`/app-devices/${row.id}`)}
          className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
        >
          {row.platformIcon}
          <span className="font-medium">{row.name}</span>
        </button>
      ),
    },
    {
      key: 'platform',
      label: 'Platform',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.platform}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <StatusBadge
          status={
            row.status === 'active' ? 'Online'
              : row.status === 'revoked' ? 'Revoked'
              : row.status === 'suspended' ? 'Disconnect'
              : 'Offline'
          }
        />
      ),
    },
    {
      key: 'assignedIp',
      label: 'VPN IP',
      render: (row) => (
        <span className="text-sm font-mono text-gray-700">
          {row.assignedIp || (row.connectedViaAccess
            ? <span className="text-gray-400" title="An Access device has no VPN address of its own">—</span>
            : row.connectedEdge ? <span className="text-gray-400 italic">Dynamic</span> : <span className="text-gray-400">Not connected</span>)}
        </span>
      ),
    },
    {
      key: 'tunnelProtocol',
      label: 'Protocol',
      render: (row) => (
        <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
          {appDeviceProtocolLabel(row)}
        </span>
      ),
    },
    {
      key: 'connectedEdge',
      label: 'Connected To',
      render: (row) => (
        <div className="flex items-center space-x-1">
          {row.connectedEdge ? (
            <>
              <Activity className="w-3 h-3 text-green-500" />
              <span className="text-sm text-gray-600 whitespace-nowrap" data-testid={row.connectedViaAccess ? 'app-device-access-gateway' : undefined}>
                {row.connectedEdge}
              </span>
              {row.connectedViaAccess && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700" title="Connected to an Access gateway (Secure Access)">
                  ZTNA
                </span>
              )}
            </>
          ) : (
            <span className="text-sm text-gray-400">Not connected</span>
          )}
        </div>
      ),
    },
    {
      key: 'dataTransfer',
      label: 'Data Transfer',
      render: (row) => (
        <span className="text-xs text-gray-600 font-mono">{row.dataTransfer}</span>
      ),
    },
    {
      key: 'lastSeen',
      label: 'Last Seen',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.lastSeen}</span>
      ),
    },
    {
      key: 'groupName',
      label: 'Group',
      render: (row) => (
        <span className={`text-sm ${row.groupName === 'Unassigned' ? 'text-gray-400' : 'text-gray-600'}`}>
          {row.groupName}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/app-devices/${row.id}`);
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors group"
            title="View & Edit"
          >
            <Edit className="w-4 h-4 text-gray-400 group-hover:text-primary-600" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">VPN App Devices</h1>
          <p className="mt-1 text-gray-600">
            Mobile and desktop devices connected via VPN
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchDevices}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          {canCreate && (
            <button
              onClick={() => setIsInviteModalOpen(true)}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <UserPlus className="w-4 h-4" />
              <span>Invite VPN Users</span>
            </button>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">VPN App Devices</h3>
              <ProtocolMixStats devices={devices} />
            </div>
          </div>
          {orgCode && (
            <div className="flex items-center space-x-2 bg-white border border-blue-200 rounded-lg px-3 py-2">
              <span className="text-xs text-gray-500">Organisation Code</span>
              <span className="font-mono text-sm font-semibold text-indigo-700">{orgCode}</span>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard icon={Monitor} title="Total Devices" value={devices.length} color="primary" />
        <StatCard icon={Activity} title="Active" value={devices.filter((d) => d.status === 'active').length} color="success" />
        <StatCard icon={Shield} title="Connected" value={devices.filter((d) => d.connectedEdge).length} color="info" />
        <StatCard icon={AlertCircle} title="Suspended" value={devices.filter((d) => d.status === 'suspended').length} color="secondary" />
      </div>

      {/* Devices Table */}
      <div className="card">
        <DataTable
          columns={columns}
          data={devices}
          loading={loading}
          emptyMessage="No VPN app devices found. Register a new device to get started."
        />
      </div>

      <InviteVPNUsersModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteUsers}
        tenantId={tenantId || ''}
        tenantName={tenantName}
      />
    </div>
  );
};

/** Shows protocol breakdown stats from device list */
const ProtocolMixStats = ({ devices }: { devices: DeviceRow[] }) => {
  if (devices.length === 0) return (
    <p className="mt-1 text-sm text-blue-700">
      Mobile and desktop devices connected via the VPN app.
    </p>
  );

  const accessCount = devices.filter(isAccessPlaneDevice).length;
  const ikev2Count = devices.filter(d => !isAccessPlaneDevice(d) && d.tunnelProtocol === 'ikev2').length;
  const wgCount = devices.length - accessCount - ikev2Count;

  const parts: string[] = [];
  if (ikev2Count > 0) parts.push(`${ikev2Count} IKEv2`);
  if (wgCount > 0) parts.push(`${wgCount} WireGuard`);
  if (accessCount > 0) parts.push(`${accessCount} Secure Access`);

  return (
    <p className="mt-1 text-sm text-blue-700">
      {devices.length} device{devices.length !== 1 ? 's' : ''} — {parts.join(', ')}
    </p>
  );
};

export default AppDevices;
